import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const THRESHOLD_PX = 64;
const MAX_PULL_PX = 96;

type PullToRefreshProps = {
  children: ReactNode;
  onRefresh: () => void | Promise<void>;
  /** Disable while a nested scroll surface should own gestures. */
  disabled?: boolean;
  className?: string;
};

function pageScrollTop(): number {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function isVerticallyScrollable(el: HTMLElement): boolean {
  const overflowY = getComputedStyle(el).overflowY;
  if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") return false;
  return el.scrollHeight - el.clientHeight > 1;
}

/** True when window or any scroll parent of the touch target is not at the top. */
function scrolledAwayFromTop(target: EventTarget | null): boolean {
  if (pageScrollTop() > 1) return true;
  let el: HTMLElement | null =
    target instanceof HTMLElement ? target : target instanceof Element ? (target.parentElement as HTMLElement | null) : null;
  while (el) {
    if (isVerticallyScrollable(el) && el.scrollTop > 1) return true;
    el = el.parentElement;
  }
  return false;
}

function ptrBlocked(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("[data-ptr-ignore]") ||
      target.closest('[role="dialog"]') ||
      target.closest("[data-vaul-drawer]"),
  );
}

/**
 * Touch pull-to-refresh for Home Screen / PWA (owner, team member, and cleaner).
 * Window-level listeners also honor nested overflow scrollers (property tabs).
 */
export function PullToRefresh({
  children,
  onRefresh,
  disabled = false,
  className,
}: PullToRefreshProps) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const setPullBoth = useCallback((n: number) => {
    pullRef.current = n;
    setPull(n);
  }, []);

  useEffect(() => {
    if (disabled) return;

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      const target = e.target;
      if (ptrBlocked(target) || scrolledAwayFromTop(target)) {
        pulling.current = false;
        return;
      }
      startY.current = e.touches[0]?.clientY ?? 0;
      pulling.current = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!pulling.current || refreshingRef.current) return;
      if (scrolledAwayFromTop(e.target)) {
        pulling.current = false;
        setPullBoth(0);
        return;
      }
      const y = e.touches[0]?.clientY ?? 0;
      const dy = y - startY.current;
      if (dy <= 0) {
        setPullBoth(0);
        return;
      }
      const resisted = Math.min(dy * 0.42, MAX_PULL_PX);
      setPullBoth(resisted);
      if (resisted > 10) {
        e.preventDefault();
      }
    };

    const onEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      const distance = pullRef.current;
      if (distance < THRESHOLD_PX || refreshingRef.current) {
        setPullBoth(0);
        return;
      }
      refreshingRef.current = true;
      setRefreshing(true);
      setPullBoth(THRESHOLD_PX * 0.7);
      void (async () => {
        try {
          await onRefreshRef.current();
        } finally {
          refreshingRef.current = false;
          setRefreshing(false);
          setPullBoth(0);
        }
      })();
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [disabled, setPullBoth]);

  const showIndicator = pull > 6 || refreshing;
  const armed = pull >= THRESHOLD_PX || refreshing;

  return (
    <div className={cn("relative", className)}>
      <div
        className="pointer-events-none fixed left-0 right-0 z-[60] flex justify-center transition-[padding]"
        style={{
          paddingTop: `calc(${Math.max(pull * 0.35, refreshing ? 10 : 0)}px + env(safe-area-inset-top, 0px))`,
          opacity: showIndicator ? 1 : 0,
        }}
        aria-hidden={!showIndicator}
      >
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-full border-[1.5px] bg-white shadow-sm",
            armed ? "border-[#24a89a] text-[#15776d]" : "border-[#e6edf1] text-[#94a3b8]",
          )}
        >
          <Loader2
            className={cn("h-5 w-5", (refreshing || armed) && "animate-spin")}
            style={
              refreshing
                ? undefined
                : { transform: `rotate(${Math.min(pull / THRESHOLD_PX, 1) * 180}deg)` }
            }
          />
        </div>
      </div>
      <div
        style={{
          transform: pull > 0 || refreshing ? `translateY(${Math.min(pull, MAX_PULL_PX) * 0.35}px)` : undefined,
          transition: pulling.current ? "none" : "transform 160ms ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );
}
